<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <a-alert type="info" show-icon class="archive-callout">
      <template slot="message">About archived webpages</template>
      <template slot="description">
        Archiving stops monitoring for a webpage and frees up its plan slot, while keeping its alerts,
        policies and history for audit purposes. Archived webpages are hidden from the Active tab,
        dashboards and notifications, and appear only here.
        Use <strong>Restore</strong> to bring a webpage back into monitoring, or <strong>Delete</strong> to permanently remove it and all of its retained data.
      </template>
    </a-alert>
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>
    <transition name="slide-up">
      <div v-if="!isLoading" class="a-filter">
        <a-row class="full-w" type="flex" justify="start" align="middle" :gutter="24">
          <a-col :span="6">
            <div class="full-w a-input-group" :class="{ active: !!filterParams.search }">
              <a-button size="large">
                <a-icon type="search" />
              </a-button>
              <a-input class="custom-prefix" placeholder="Search" @input="onSearch" />
            </div>
          </a-col>
          <a-col>
            <a-checkbox v-model="filterParams.exactMatch" @change="onChangeExactMatch">
              Exact match
            </a-checkbox>
          </a-col>
          <a-col :span="8">
            <div class="full-w a-select-group" :class="{ active: hasCombinedFilterActive }">
              <a-button size="large">
                <a-icon type="filter" />
              </a-button>
              <a-select
                v-model="filterParams.combinedFilter"
                mode="multiple"
                :size="'large'"
                placeholder="Filter by Previous Plan, Tags..."
                :maxTagCount="2"
                :maxTagTextLength="12"
                @change="onChangeCombinedFilter"
              >
                <a-select-opt-group label="Previous Plan">
                  <a-select-option v-for="planType in planTypeOptions" :key="'planType:' + planType.value" :value="'planType:' + planType.value">
                    {{ planType.label }}
                  </a-select-option>
                </a-select-opt-group>
                <a-select-opt-group v-if="isTagsFeatureEnabled" label="Tags">
                  <a-select-option v-for="tag in availableTags" :key="'tag:' + tag" :value="'tag:' + tag">
                    {{ tag }}
                  </a-select-option>
                </a-select-opt-group>
              </a-select>
            </div>
          </a-col>
        </a-row>
        <a-row class="full-w mt-2" type="flex" justify="start">
          <a-button size="default" :disabled="!isHasSelected" class="restore-btn" @click="onBulkRestore()">
            <a-icon type="undo" />
            Restore
          </a-button>
          <a-button size="default" :disabled="!isHasSelected" type="danger" class="ml-2 danger-btn" @click="onBulkDelete()">
            <a-icon type="delete" />
            Delete
          </a-button>
        </a-row>
      </div>
    </transition>
    <div v-resize="elSizeChange" class="a-body overflow-y-hidden flex-grow-1">
      <transition name="slide-up">
        <template v-if="!isLoading">
          <PaginationTable
            :clientPaging="false"
            :hasSelectRow="true"
            :pagination="pagin"
            :columns="ArchivedWebpageColumns"
            :dataSource="data"
            :tableSize="tableSize"
            @changeSelectedRow="onSelectChange"
          >
            <div slot="urls" slot-scope="{ record }">
              <router-link :to="'/webpages/' + record.id" class="url text-wrap">
                {{ record.url_name }}
              </router-link>
            </div>
            <template slot="tags" slot-scope="{ record }">
              <span v-if="Array.isArray(record.tags) && record.tags.length">
                <a-tag v-for="tag in record.tags" :key="tag.tagID" class="mr-1 mb-1">
                  {{ tag.name }}
                </a-tag>
              </span>
              <span v-else>-</span>
            </template>
            <template slot="prev_plan" slot-scope="{ record }">
              {{ (record.archive_detail && record.archive_detail.prev_plan_type || '-') | lowercase | capitalize }}
            </template>
            <template slot="remark" slot-scope="{ record }">
              <span class="text-wrap">{{ (record.archive_detail && record.archive_detail.remark) || '-' }}</span>
            </template>
            <div slot="archived_on" slot-scope="{ record }">
              <p class="time-text text-truncate">
                <time>{{ archivedAt(record) | dateString }}</time>
              </p>
              <p class="time-text text-truncate">
                <time>{{ archivedAt(record) | timeString }}</time>
              </p>
            </div>
            <template slot="time_since_archive" slot-scope="{ record }">
              <a-tooltip :title="archivedAt(record) | dateString">
                <a-tag class="time-since-tag">{{ timeSinceArchive(record) }}</a-tag>
              </a-tooltip>
            </template>
            <template slot="operation" slot-scope="{ record }">
              <a-dropdown placement="topLeft">
                <a-icon class="operation" type="ellipsis" />
                <a-menu slot="overlay" class="operation-menu">
                  <a-menu-item @click="onRestore(record)">
                    <a-icon type="undo" class="mr-2" />
                    Restore
                  </a-menu-item>
                  <a-menu-item @click="showDeleteConfirm(record)">
                    <img class="mr-2" src="@assets/icon/delete.svg" />
                    Delete
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </template>
          </PaginationTable>
        </template>
      </transition>
    </div>
    <div class="a-footer">
      <PaginationBar :pageSize.sync="pagin.pageSize" :total="pagin.total" storageKey="archivedPageSize" @change="paginChange" />
    </div>
    <a-modal
      v-model="restoreEdit.isVisible"
      style="top: 20px;"
      width="80vw"
      class="restore-edit-modal"
      title="Restore Webpages - Choose Plan and Polling Interval"
      cancelText="Close"
      okText="Restore"
      :cancelButtonProps="{ style: { float: 'right' } }"
      :okButtonProps="{
        props: {
          disabled: restoreEdit.isSubmitting,
          loading: restoreEdit.isSubmitting
        },
        style: {
          marginRight: '8px',
          opacity: restoreEdit.isSuccess ? '0' : '1',
        },
      }"
      :afterClose="afterCloseRestoreModal"
      :closable="false"
      :maskClosable="false"
      @ok="submitRestore"
    >
      <div v-if="!restoreEdit.isSuccess">
        <a-row :gutter="24" class="mb-4">
          <a-col :span="8">
            <a-form-item label="Plan Type" class="mb-0">
              <a-select
                v-model="restoreEdit.form.planType"
                size="large"
                allowClear
                placeholder="Keep each webpage's original plan"
                @change="onRestorePlanTypeChange"
              >
                <a-select-option v-for="planType in planTypeOptions" :key="planType.value" :value="planType.value">
                  {{ planType.label }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="8">
            <a-form-item label="Polling Interval" class="mb-0">
              <a-select
                v-model="restoreEdit.form.pollingInterval"
                size="large"
                :disabled="!restoreEdit.form.planType"
                placeholder="Keep each webpage's original interval"
              >
                <a-select-option v-for="(label, value) in restoreEdit.pollingIntervalOptions" :key="+value" :value="+value">
                  {{ label | timeText }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>
        <p class="text-muted mb-2">
          <a-icon type="info-circle" class="mr-1" />
          Leave the plan blank to restore each webpage to its original plan and polling interval (shown below).
          Choosing a plan applies it to every webpage instead. Restored webpages resume verification
          automatically.
        </p>
        <PaginationTable
          :columns="RestoreWebpageColumns"
          :dataSource="restoreEdit.data"
          :pagination="restoreEdit.pagination"
        >
          <template slot="webpageUrl" slot-scope="{ record }">
            <a class="url" :href="record.url" target="_blank" rel="noopener noreferrer">
              {{ record.url }}
            </a>
          </template>
          <template slot="planType" slot-scope="{ record }">
            <p class="mb-0">
              {{ record.plan_type | lowercase | capitalize }}
            </p>
          </template>
          <template slot="pollingInterval" slot-scope="{ record }">
            {{ POLLING_INTERVAL[record.polling_interval] | timeText }}
          </template>
        </PaginationTable>
        <PaginationBar
          :pageSize.sync="restoreEdit.pagination.pageSize"
          :total="restoreEdit.pagination.total"
          storageKey="restoreEditPageSize"
          @change="onRestorePaginChange"
        />
      </div>
      <div v-if="restoreEdit.isSuccess && restoreEdit.result">
        <div class="success-restore-container" :class="{ 'has-errors': restoreEdit.result.summary && restoreEdit.result.summary.failed > 0 }">
          <div>
            <a-icon
              :type="restoreEdit.result.summary && restoreEdit.result.summary.failed > 0 ? 'exclamation-circle' : 'check-circle'"
              theme="filled"
              :class="restoreEdit.result.summary && restoreEdit.result.summary.failed > 0 ? 'check-warning' : 'check-success'"
            />
          </div>
          <div class="ml-3">
            <p class="mb-1 notice-text">{{ restoreEdit.result.message }}</p>
            <p v-if="restoreEdit.result.summary && restoreEdit.result.summary.restored > 0" class="mb-0 notice-text">
              Verification will resume automatically with the last baseline of each restored webpage.
            </p>
          </div>
        </div>

        <!-- Error details section -->
        <div v-if="restoreEdit.result.errors && restoreEdit.result.errors.length > 0" class="restore-errors mt-4">
          <p class="error-title mb-2">Failed to restore the following URLs:</p>
          <div class="error-list">
            <div v-for="(err, index) in restoreEdit.result.errors" :key="index" class="error-item">
              <div class="error-url">{{ err.url }}</div>
              <div class="error-message">{{ err.error }}</div>
            </div>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
  import { debounce } from 'lodash';
  import moment from 'moment-timezone';
  import { formatUtcTime } from '@shared/helpers/utils';
  import { POLLING_INTERVAL } from '@constants/pollingInterval';
  import { ArchivedWebpageColumns } from '../columnsTable/ArchivedWebpageColumns';
  import { RestoreWebpageColumns } from '../columnsTable/RestoreWebpageColumns';
  import WebpageService from '../services/webpage.service';
  import webpageListMixin from '../mixins/webpageList.mixin';

  export default {
    name: 'ArchivedWebpageList',
    mixins: [webpageListMixin],
    data() {
      let pageSize = Number(window.localStorage.getItem('archivedPageSize'));
      if (!pageSize) {
        pageSize = 5;
      }

      return {
        ArchivedWebpageColumns,
        RestoreWebpageColumns,
        POLLING_INTERVAL,
        data: [],
        filterParams: {
          search: '',
          exactMatch: false,
          combinedFilter: []  // Array of 'planType:X', 'tag:X' values
        },
        selectedRowKeys: [],
        // Full archived list, fetched once and cached to resolve cross-page bulk
        // selections; invalidated (set to null) after restore/delete. Mirrors the
        // active list's allUrlRecords / fetchAllUrlRecords() convention.
        allArchivedRecords: null,
        pagin: {
          current: 1,
          pageSize: pageSize,
          total: 0,
        },
        isLoading: true,
        tableSize: {
          height: '',
          width: '',
        },
        restoreEdit: {
          isVisible: false,
          data: [],
          form: { planType: null, pollingInterval: null },
          pollingIntervalOptions: {},
          isSubmitting: false,
          isSuccess: false,
          result: null,
          pagination: { current: 1, pageSize: 5, total: 0 }
        },
      };
    },
    async mounted() {
      await this.loadEnums();
      this.getList();
    },
    methods: {
      archivedAt(record) {
        return (record.archive_detail && record.archive_detail.archived_at) || null;
      },
      timeSinceArchive(record) {
        const archivedAt = this.archivedAt(record);
        if (!archivedAt) {
          return '-';
        }
        return moment.utc(formatUtcTime(archivedAt)).fromNow();
      },
      onSearch: debounce(function (event) {
        this.runSearch(event);
      }, 800),
      async getList() {
        try {
          const { planTypes, tags } = this.parsedCombinedFilter;
          const [response, tagsData] = await Promise.all([
            WebpageService.getList({
              page: this.pagin.current,
              itemsPerPage: this.pagin.pageSize,
              q: this.filterParams.search || '',
              exact: this.filterParams.exactMatch,
              planTypes: planTypes,
              tagIds: this.getTagIdsByNames(tags),
              archived: true
            }),
            this.loadTagsIfNeeded()
          ]);

          this.applyTagsData(tagsData);

          const items = response.data || response || [];
          this.data = this.mapListRows(items);

          if (response.meta) {
            this.pagin.total = response.meta.total;
          } else {
            this.pagin.total = items.length;
          }
        } catch (error) {
          console.error('Error fetching archived webpages:', error);
          this.$showErrorMessage('Failed to load archived webpages. Please try again.');
        } finally {
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }
        }
      },
      onRestore(record) {
        this.openRestoreCard([record]);
      },
      async onBulkRestore() {
        if (this.selectedRowKeys.length === 0) return;
        try {
          const records = await this.getSelectedArchivedRecords();
          this.openRestoreCard(records);
        } catch (error) {
          this.$showErrorMessage('Failed to load selected webpages. Please try again.');
        }
      },
      /**
       * Fetches the full archived list (with archiveDetail) and caches it.
       * Subsequent calls return the cached list until invalidated.
       */
      async fetchAllArchivedRecords() {
        if (!this.allArchivedRecords) {
          const allItems = await WebpageService.getList({ archived: true, itemsPerPage: 1000 });
          this.allArchivedRecords = Array.isArray(allItems) ? allItems : [];
        }
        return this.allArchivedRecords;
      },
      async getSelectedArchivedRecords() {
        const allRecords = await this.fetchAllArchivedRecords();
        return allRecords.filter((item) => this.selectedRowKeysSet.has(item.id));
      },
      // Same card as the active tab's bulk plan-type edit: optionally pick one plan + polling
      // interval to apply to all listed webpages. Opens blank so submitting as-is restores each
      // webpage to its own original plan and polling interval (per-URL error report on submit).
      openRestoreCard(records) {
        this.restoreEdit.data = records.map((record, index) => ({
          id: record.id,
          index: index + 1,
          url: record.url_name,
          plan_type: (record.archive_detail && record.archive_detail.prev_plan_type) || null,
          polling_interval: record.polling_interval,
        }));

        this.restoreEdit.form = { planType: null, pollingInterval: null };
        this.restoreEdit.pollingIntervalOptions = {};
        this.restoreEdit.isSuccess = false;
        this.restoreEdit.isSubmitting = false;
        this.restoreEdit.result = null;
        const savedPageSize = Number(window.localStorage.getItem('restoreEditPageSize')) || 5;
        this.restoreEdit.pagination = { current: 1, pageSize: savedPageSize, total: this.restoreEdit.data.length };

        this.restoreEdit.isVisible = true;
      },
      onRestorePaginChange(pagin) {
        this.restoreEdit.pagination = { ...pagin, total: this.restoreEdit.data.length };
      },
      onRestorePlanTypeChange(planType) {
        this.restoreEdit.pollingIntervalOptions = this.applyPollingOptionsForPlan(this.restoreEdit.form, planType);
      },
      async submitRestore() {
        const { planType, pollingInterval } = this.restoreEdit.form;

        // No plan chosen → send an empty body so the API restores each webpage onto its own
        // original plan type and polling interval. A chosen plan is applied to the whole batch.
        const body = {};
        if (planType) {
          body.plan_type = planType;
          if (pollingInterval) {
            body.polling_interval = pollingInterval;
          }
        }

        this.restoreEdit.isSubmitting = true;

        const urlIds = this.restoreEdit.data.map((row) => row.id);
        // Map ids to display urls so per-URL API errors (keyed by url_id) can be shown by url.
        const urlById = {};
        this.restoreEdit.data.forEach((row) => {
          urlById[row.id] = row.url;
        });

        try {
          const result = await WebpageService.batchRestore(urlIds, body);

          const apiErrors = (result && result.errors) || [];
          const summary = (result && result.summary) || {};
          const errors = apiErrors.map((e) => ({
            url: urlById[e.url_id] || e.url_id,
            error: e.error
          }));
          const totalRequested = summary.total_requested != null ? summary.total_requested : urlIds.length;
          const restoredCount = summary.restored != null ? summary.restored : (urlIds.length - errors.length);
          const failedCount = summary.failed != null ? summary.failed : errors.length;

          this.restoreEdit.result = {
            message: (result && result.message) || `${restoredCount} of ${totalRequested} webpage(s) restored successfully`,
            summary: { total_requested: totalRequested, restored: restoredCount, failed: failedCount },
            errors
          };
          this.restoreEdit.isSuccess = true;

          // Keep failed webpages selected so the user can retry with another plan
          const failedIdSet = new Set(apiErrors.map((e) => e.url_id));
          const restoredIdSet = new Set(urlIds.filter((id) => !failedIdSet.has(id)));
          this.selectedRowKeys = this.selectedRowKeys.filter((key) => !restoredIdSet.has(key));
          this.allArchivedRecords = null;
          this.getList();
          if (restoredCount > 0) {
            this.$emit('restored');
          }
        } catch (error) {
          this.$showErrorMessage('Failed to restore webpage(s). Please try again.');
        } finally {
          this.restoreEdit.isSubmitting = false;
        }
      },
      afterCloseRestoreModal() {
        this.restoreEdit.isSuccess = false;
        this.restoreEdit.form = { planType: null, pollingInterval: null };
        this.restoreEdit.pollingIntervalOptions = {};
        this.restoreEdit.result = null;
        this.restoreEdit.data = [];
        this.restoreEdit.pagination.current = 1;
        this.restoreEdit.pagination.total = 0;
      },
      showDeleteConfirm(record) {
        this.$confirm({
          title: 'Permanently delete this archived webpage?',
          content: `${record.url_name} and all of its retained data (alerts, policy assignments, verification history) will be permanently removed. This cannot be undone.`,
          okText: 'Delete',
          okType: 'danger',
          cancelText: 'Cancel',
          onOk: async () => {
            try {
              await WebpageService.delete(record.id);
              this.$showSuccessMessage('Archived webpage deleted');
              this.selectedRowKeys = this.selectedRowKeys.filter((key) => key !== record.id);
              this.allArchivedRecords = null;
              return this.getList();
            } catch (error) {
              this.$showErrorMessage('Failed to delete archived webpage. Please try again.');
            }
          },
        });
      },
      onBulkDelete() {
        const selectedCount = this.selectedRowKeys.length;
        if (selectedCount === 0) return;
        this.$confirm({
          title: `Permanently delete ${selectedCount} archived webpage(s)?`,
          content: 'The webpages and all of their retained data (alerts, policy assignments, verification history) will be permanently removed. This cannot be undone.',
          okText: 'Delete',
          okType: 'danger',
          cancelText: 'Cancel',
          onOk: async () => {
            // Delete each independently so one failure doesn't abort the rest;
            // collect per-item results and report a summary (mirrors the restore flow).
            const ids = [...this.selectedRowKeys];
            const outcomes = await Promise.all(
              ids.map((id) =>
                WebpageService.delete(id)
                  .then(() => ({ id, ok: true }))
                  .catch(() => ({ id, ok: false }))
              )
            );

            const failedIds = outcomes.filter((o) => !o.ok).map((o) => o.id);
            const successCount = ids.length - failedIds.length;

            if (failedIds.length === 0) {
              this.$showSuccessMessage('Archived webpages deleted');
            } else if (successCount === 0) {
              this.$showErrorMessage('Failed to delete the selected archived webpage(s). Please try again.');
            } else {
              this.$showWarningMessage(`Deleted ${successCount} of ${ids.length} webpage(s); ${failedIds.length} failed. Please retry the remaining.`);
            }

            // Keep only the rows that failed selected so the user can retry them.
            this.selectedRowKeys = failedIds;
            this.allArchivedRecords = null;
            return this.getList();
          },
        });
      },
      elSizeChange(size) {
        this.tableSize = size;
      },
    }
  };
</script>

<style lang="scss" scoped>
  .archive-callout {
    margin: 0 24px 12px;
  }
  .url {
    display: block;
  }
  .time-text {
    margin-bottom: 0;
  }
  .time-since-tag {
    color: #fff;
    background: #595959;
    border-color: transparent;
  }
  .restore-btn {
    border-color: transparent;
    &:not([disabled]) {
      color: #f5f5f5;
      background-color: #5e9626;
    }
  }
  .danger-btn {
    border-color: transparent;
    &:hover {
      border-color: transparent;
    }
    &:not([disabled]) {
      color: #f5f5f5;
      background-color: #f5222d;
    }
  }
  .check-success {
    font-size: 40px;
    color: rgb(126, 207, 66);
  }
  .check-warning {
    font-size: 40px;
    color: #faad14;
  }
  .success-restore-container {
    display: flex;
    align-items: center;
    .notice-text {
      font-size: 1rem;
    }
  }
  .restore-errors {
    border-top: 1px solid #f0f0f0;
    padding-top: 16px;
    .error-title {
      font-weight: 600;
      color: #595959;
    }
    .error-list {
      max-height: 200px;
      overflow-y: auto;
    }
    .error-item {
      padding: 8px 12px;
      background-color: #fff2f0;
      border: 1px solid #ffccc7;
      border-radius: 4px;
      margin-bottom: 8px;
      &:last-child {
        margin-bottom: 0;
      }
    }
    .error-url {
      font-weight: 500;
      color: #262626;
      word-break: break-all;
      margin-bottom: 4px;
    }
    .error-message {
      color: #cf1322;
      font-size: 0.875rem;
    }
  }
  .text-muted {
    color: #8c8c8c;
    font-size: 0.875rem;
  }
</style>
